import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';

function Setup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [dbConfig, setDbConfig] = useState({
    dbHost: 'localhost',
    dbPort: 5432,
    dbName: '',
    dbUser: '',
    dbPassword: '',
    useExisting: false
  });

  const [tenantConfig, setTenantConfig] = useState({
    companyName: '',
    adminEmail: '',
    adminPassword: '',
    plan: '1',
    primaryColor: '#6366f1',
    logo: null
  });

  const handleDatabaseSetup = async () => {
    setLoading(true);
    try {
      const response = await api.post('/setup/database', dbConfig);
      if (response.data.success) {
        toast.success('Banco de dados configurado com sucesso!');
        setStep(2);
      }
    } catch (error) {
      toast.error('Erro ao configurar banco de dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTenantSetup = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(tenantConfig).forEach(key => {
        formData.append(key, tenantConfig[key]);
      });

      const response = await api.post('/setup/tenant', formData);
      if (response.data.success) {
        toast.success('Tenant criado com sucesso!');
        navigate('/login');
      }
    } catch (error) {
      toast.error('Erro ao criar tenant');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center p-4">
      <div className="card rounded-xl p-8 max-w-2xl w-full">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Configuração Inicial</h1>
          <div className="mt-4 flex space-x-2">
            <div className={`flex-1 h-2 rounded ${step >= 1 ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
            <div className={`flex-1 h-2 rounded ${step >= 2 ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Configuração do Banco de Dados</h2>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="useExisting"
                checked={dbConfig.useExisting}
                onChange={(e) => setDbConfig({...dbConfig, useExisting: e.target.checked})}
                className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="useExisting" className="text-sm text-gray-700 dark:text-gray-300">Usar banco de dados existente</label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">
                  Host do Banco
                </label>
                <input
                  type="text"
                  value={dbConfig.dbHost}
                  onChange={(e) => setDbConfig({...dbConfig, dbHost: e.target.value})}
                  className="input"
                />
              </div>

              <div>
                <label className="label">
                  Porta
                </label>
                <input
                  type="number"
                  value={dbConfig.dbPort}
                  onChange={(e) => setDbConfig({...dbConfig, dbPort: e.target.value})}
                  className="input"
                />
              </div>
            </div>

            <div>
              <label className="label">
                Nome do Banco
              </label>
              <input
                type="text"
                value={dbConfig.dbName}
                onChange={(e) => setDbConfig({...dbConfig, dbName: e.target.value})}
                placeholder="monitoring_tenant1"
                className="input"
              />
            </div>

            <div>
              <label className="label">
                Usuário
              </label>
              <input
                type="text"
                value={dbConfig.dbUser}
                onChange={(e) => setDbConfig({...dbConfig, dbUser: e.target.value})}
                className="input"
              />
            </div>

            <div>
              <label className="label">
                Senha
              </label>
              <input
                type="password"
                value={dbConfig.dbPassword}
                onChange={(e) => setDbConfig({...dbConfig, dbPassword: e.target.value})}
                className="input"
              />
            </div>

            <button
              onClick={handleDatabaseSetup}
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? 'Configurando...' : 'Configurar Banco de Dados'}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Configuração da Empresa</h2>

            <div>
              <label className="label">
                Nome da Empresa
              </label>
              <input
                type="text"
                value={tenantConfig.companyName}
                onChange={(e) => setTenantConfig({...tenantConfig, companyName: e.target.value})}
                className="input"
              />
            </div>

            <div>
              <label className="label">
                Email do Administrador
              </label>
              <input
                type="email"
                value={tenantConfig.adminEmail}
                onChange={(e) => setTenantConfig({...tenantConfig, adminEmail: e.target.value})}
                className="input"
              />
            </div>

            <div>
              <label className="label">
                Senha do Administrador
              </label>
              <input
                type="password"
                value={tenantConfig.adminPassword}
                onChange={(e) => setTenantConfig({...tenantConfig, adminPassword: e.target.value})}
                className="input"
              />
            </div>

            <div>
              <label className="label">
                Plano
              </label>
              <select
                value={tenantConfig.plan}
                onChange={(e) => setTenantConfig({...tenantConfig, plan: e.target.value})}
                className="input"
              >
                <option value="1">Plano 1 - Dashboard</option>
                <option value="2">Plano 2 - Dashboard + Análises</option>
                <option value="3">Plano 3 - Completo com IA Chat</option>
              </select>
            </div>

            <div>
              <label className="label">
                Cor Principal
              </label>
              <input
                type="color"
                value={tenantConfig.primaryColor}
                onChange={(e) => setTenantConfig({...tenantConfig, primaryColor: e.target.value})}
                className="w-full h-12 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
              />
            </div>

            <div>
              <label className="label">
                Logo da Empresa
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setTenantConfig({...tenantConfig, logo: e.target.files[0]})}
                className="input"
              />
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setStep(1)}
                className="flex-1 btn-outline"
              >
                Voltar
              </button>
              <button
                onClick={handleTenantSetup}
                disabled={loading}
                className="flex-1 btn-primary"
              >
                {loading ? 'Criando...' : 'Criar Tenant'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Setup;
